import { useState, useEffect } from 'react'
import { isValidContactInfoFilter } from '@/features/leads/workflow/queue'
import type { ContactInfoFilter } from '@/features/leads/workflow/queue'
import { clearStored, readStored, writeStored } from '@/shared/lib/storage'
import { isValidFocusSort, type FocusSort } from './focusSort'
const CONTACT_INFO_KEY = 'focus.contactInfo'
// Versioned and new on purpose: 'focus.sort' / 'crm.focusSort' held the
// pre-CRM-84 score-or-bands choice, whose values this one does not share.
const SORT_KEY = 'focus.queueSort.v1'

function loadFocusContactInfo(): ContactInfoFilter {
  const v = readStored(CONTACT_INFO_KEY, { legacyKey: 'crm.focusContactInfo' })
  return isValidContactInfoFilter(v) ? v : 'all'
}

function loadFocusSort(): FocusSort {
  const v = readStored(SORT_KEY)
  return isValidFocusSort(v) ? v : 'standard'
}

interface FocusPrefs {
  focusContactInfo: ContactInfoFilter
  setFocusContactInfo: React.Dispatch<React.SetStateAction<ContactInfoFilter>>
  focusSort: FocusSort
  setFocusSort: React.Dispatch<React.SetStateAction<FocusSort>>
}

export function useFocusPrefs(): FocusPrefs {
  // Three-state contact-info filter for the focus queue — 'all' (default)
  // is the working queue; 'reachable' hides leads with no contact info;
  // 'unreachable' shows ONLY those (the "find me a contact" bucket). State
  // persists so a BDR who's working unreachables across breaks doesn't
  // lose their view. Table view has its own copy under table.viewState.
  const [focusContactInfo, setFocusContactInfo] = useState<ContactInfoFilter>(loadFocusContactInfo)
  useEffect(() => {
    writeStored(CONTACT_INFO_KEY, focusContactInfo)
  }, [focusContactInfo])

  // The order the seller works their queue in: the banded default, AI score
  // or lead date (YCRM-40). Lives here rather than in the view so it survives
  // switching to Tabell and back as well as a reload.
  const [focusSort, setFocusSort] = useState<FocusSort>(loadFocusSort)
  useEffect(() => {
    writeStored(SORT_KEY, focusSort)
  }, [focusSort])

  // The keys of the old pre-CRM-84 choice. Nothing reads them, so drop them
  // rather than leave stale values in every seller's browser.
  useEffect(() => {
    clearStored('focus.sort')
    clearStored('crm.focusSort')
  }, [])

  return { focusContactInfo, setFocusContactInfo, focusSort, setFocusSort }
}
